import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { num } from '../lib/format.js';
import { hrefFor } from '../lib/router.js';
import {
  EmptyState,
  ErrorState,
  LoadingState,
  Panel,
  StatusBadge,
  Tile,
} from '../components/ui.js';
import '../screens-2c.css';
import { SOURCE_PARAM } from './Customers.js';

export interface FinishedRun {
  id: string;
  mode: 'dry_run' | 'apply';
  counts: Record<string, number>;
  options: { createContacts: boolean };
}

interface SampleItem {
  contactId: string;
  email: string;
  name: string | null;
  matchedToShop: boolean;
  orders: number;
  blacklisted: boolean;
  unsubscribed: boolean;
}

/** Souhrn hotového běhu pro člověka: co se stalo (nebo by se stalo) s kontakty a co s nimi bude dál. */
export function SeAfterImport({
  run,
  canSeeCustomers,
}: {
  run: FinishedRun;
  canSeeCustomers: boolean;
}): JSX.Element {
  const apply = run.mode === 'apply';
  const c = run.counts;
  const read = c.contacts ?? 0;
  const created = apply ? (c.created ?? 0) : (c.wouldCreate ?? 0);
  const matched = c.matched ?? 0;
  const skipped = Math.max(0, read - created - matched);
  const sample = useQuery<{ items: SampleItem[] }>({
    queryKey: ['se-import', 'sample', run.id],
    queryFn: () => api.get(`/api/provider-import/runs/${run.id}/sample`),
    enabled: apply,
  });

  return (
    <Panel
      title="Co se stane po importu"
      subtitle={
        apply
          ? 'Ostrý běh je hotový. Takhle dopadly kontakty.'
          : 'Běh nanečisto: nic se v hubu nezměnilo. Tohle by se stalo při ostrém běhu.'
      }
    >
      <div className="tiles" data-testid="se-after-counts">
        <Tile label="Přečteno ze SmartEmailingu" value={num(read)} />
        <Tile
          label={apply ? 'Nové kontakty v hubu' : 'Nových kontaktů by vzniklo'}
          value={num(created)}
          note="bez souhlasu"
        />
        <Tile
          label="Spárováno s kontakty, které hub už má"
          value={num(matched)}
          note="podle e-mailu"
        />
        <Tile
          label={apply ? 'Odhlášení: souhlas odvolán' : 'Odhlášených (souhlas by se odvolal)'}
          value={num(apply ? (c.withdrawn ?? 0) : (c.conflicts ?? 0))}
        />
        <Tile
          label={apply ? 'Blokovaní: potlačeni' : 'Blokovaných ve SE'}
          value={num(apply ? (c.blacklistedSet ?? 0) : (c.blacklisted ?? 0))}
        />
        <Tile
          label={apply ? 'Souhlas udělen (z vybraných seznamů)' : 'Souhlas by dostalo'}
          value={num(apply ? (c.granted ?? 0) : (c.wouldGrant ?? 0))}
        />
        <Tile
          label="Přeskočeno"
          value={num(skipped)}
          note={run.options.createContacts ? undefined : 'zakládání kontaktů je vypnuté'}
        />
      </div>

      {apply ? (
        <>
          <div className="row">
            {canSeeCustomers ? (
              <a className="btn pri" href={hrefFor('customers', SOURCE_PARAM)}>
                Zobrazit importované kontakty
              </a>
            ) : (
              <span className="note">Zákazníky nemáte oprávnění zobrazit.</span>
            )}
          </div>
          <h3 className="s2c-sub">Vzorové kontakty</h3>
          {sample.data?.items.length ? (
            <div className="scroll">
              <table>
                <thead>
                  <tr>
                    <th>Kontakt</th>
                    <th>Stav</th>
                    <th className="n">Objednávky</th>
                  </tr>
                </thead>
                <tbody>
                  {sample.data.items.map((s) => (
                    <tr key={s.contactId}>
                      <td>
                        {canSeeCustomers ? (
                          <a href={hrefFor('customers', s.contactId)}>{s.email}</a>
                        ) : (
                          s.email
                        )}
                        {s.name ? <span className="muted"> · {s.name}</span> : null}
                      </td>
                      <td>
                        {s.blacklisted ? (
                          <StatusBadge tone="bad" icon="alert">
                            blokovaný ve SE
                          </StatusBadge>
                        ) : null}
                        {s.unsubscribed ? (
                          <StatusBadge tone="warn" icon="alert">
                            odhlášen ve SE
                          </StatusBadge>
                        ) : null}
                        {s.matchedToShop ? (
                          <StatusBadge tone="ok" icon="check">
                            spárováno s e-shopem
                          </StatusBadge>
                        ) : null}
                        {!s.blacklisted && !s.unsubscribed && !s.matchedToShop ? (
                          <StatusBadge tone="neu">bez souhlasu</StatusBadge>
                        ) : null}
                      </td>
                      <td className="n">{num(s.orders)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : sample.isLoading ? (
            <LoadingState what="vzorek" />
          ) : sample.isError ? (
            <ErrorState
              error={sample.error}
              title="Vzorek kontaktů se nepodařilo načíst"
              onRetry={() => void sample.refetch()}
            />
          ) : (
            <EmptyState title="Žádný vzorek">Z tohoto běhu není žádný kontakt v hubu.</EmptyState>
          )}
        </>
      ) : null}

      <div className="help" data-testid="se-after-explain">
        <h3 className="s2c-sub">Co importované kontakty smějí a nesmějí</h3>
        <ul>
          <li>
            <b>Marketing nedostanou, dokud nemají souhlas.</b> Souhlas vznikne jen u potvrzených
            členů seznamů, které jste v kroku „Seznamy“ označili jako dokládající souhlas. Všichni
            ostatní importovaní kontakty jsou v hubu bez souhlasu.
          </li>
          <li>
            <b>Odhlášení a blokovaní jsou potlačeni.</b> Kdo je ve SmartEmailingu odhlášený z
            některého seznamu, má v hubu odvolaný souhlas a marketing mu nejde, ani kdyby e-shop
            souhlas později dal (u kontaktu je vidět „odhlášen ve SE“). Blokovaný kontakt nedostane
            žádnou zprávu, ani servisní.
          </li>
          <li>
            <b>Servisní zprávy</b> (potvrzení, vstupenky) chodí jen k cestě z e-shopu. Importovaný
            kontakt bez objednávky žádnou nemá.
          </li>
          <li>
            <b>Jak se kontakt páruje.</b> Podle e-mailové adresy, bez ohledu na velikost písmen. Nic
            se nedomýšlí: adresa s „+“ nebo jinou příponou je jiný kontakt.
          </li>
          <li>
            <b>Co udělá pozdější import z e-shopu.</b> Události se stejnou adresou se přiřadí k
            témuž kontaktu: doplní se id zákazníka, objednávky a cesty. Telefon a země z e-shopu
            přebijí hodnoty ze SmartEmailingu, jméno ze SE zůstane, kde e-shop žádné nemá. Souhlas z
            e-shopu platí, pokud je novější než odhlášení ve SE; marketing ale zůstane zastavený,
            dokud je kontakt u SmartEmailingu odhlášený.
          </li>
          <li>
            <b>Hub do SmartEmailingu nic nezapisuje.</b> Import je jen čtecí.
          </li>
        </ul>
      </div>
    </Panel>
  );
}
